<template>
  <div
    class="notifications"
    :class="mode === 'mobile' ? 'notifications--modale' : 'notifications--bottom'"
  >
    <div class="notifications__header">
      <BasicText
        weight="bold"
        size="h5"
        color="secondary-900"
      >
        Notifications ({{ totalCount }})
      </BasicText>
      <BasicToggleSwitch
        :modelValue="listToggleId"
        @update:model-value="(val) => emit('update:listToggleId', val ?? 0)"
        size="small"
        :items="[
          { id: 0, label: 'Nouveau' },
          { id: 1, label: 'Voir tout' },
        ]"
      />
    </div>
    <div
      v-if="listToggleId === 0"
      class="notifications__list"
    >
      <div
        v-for="(elem, id) in unreadList?.data"
        :key="id"
        class="notifications__elem"
      >
        <BasicIcon
          :name="IconMap[elem.type]"
          :class="`notifications__elem--${elem.type.toLocaleLowerCase()}`"
        />
        <div class="notifications__elem--content">
          <BasicText
            size="body-m"
            color="secondary-900"
            style="text-overflow: ellipsis; overflow: hidden;"
          >
            {{ elem.message }}
          </BasicText>
          <BasicText
            size="body-s"
            color="secondary-600"
          >
            {{ formatDate(elem.lastModifiedDate) }}
          </BasicText>
        </div>
        <BasicTooltip
          class="iconRight"
          label="Marquer comme lu"
          position="left"
        >
          <BasicIcon
            @click="$emit('changeNotificationStatut', elem.id, 'Read')"
            color="black"
            pointer
            name="tick-circle"
          />
        </BasicTooltip>
      </div>
    </div>
    <div
      v-else-if="listToggleId === 1"
      class="notifications__list"
    >
      <div
        v-for="(elem, id) in allList?.data"
        :key="id"
      >
        <div class="notifications__elem">
          <BasicIcon
            :name="IconMap[elem.type]"
            :class="`notifications__elem--${elem.type.toLocaleLowerCase()}`"
          />
          <div class="notifications__elem--content">
            <BasicText
              size="body-m"
              color="secondary-900"
              style="text-overflow: ellipsis; overflow: hidden;"
            >
              {{ elem.message }}
            </BasicText>
            <BasicText
              size="body-s"
              color="secondary-600"
            >
              {{ formatDate(elem.lastModifiedDate) }}
            </BasicText>
          </div>
          <BasicTooltip
            class="iconRight"
            :label="elem.statut === 'Read' ? 'Marquer comme à lire' : 'Marquer comme lu'"
            position="left"
          >
            <BasicIcon
              @click="emit('changeNotificationStatut', elem.id, elem.statut === 'Read' ? 'Unread' : 'Read')"
              color="black"
              pointer
              :name="elem.statut === 'Read' ? 'notification' : 'tick-circle'"
            />
          </BasicTooltip>
        </div>
      </div>
    </div>
    <BasicText
      v-if="listToggleId === 0"
      class="notifications__footer"
      @click="unreadList?.data.length ? emit('markAllAs', 'Read') : null"
      size="body-m"
      color="white"
      :pointer="!!unreadList?.data.length"
    >
      {{ unreadList?.data.length ? 'Tout marquer comme lu' : 'Pas de notifications' }}
    </BasicText>
    <BasicText
      v-else-if="listToggleId === 1"
      class="notifications__footer"
      @click="allList?.data.length ? emit('markAllAs', 'Unread') : null"
      size="body-m"
      color="white"
      :pointer="!!allList?.data.length"
    >
      {{ allList?.data.length ? 'Tout marquer comme à lire' : 'Pas de notifications' }}
    </BasicText>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import moment from 'moment/min/moment-with-locales'
  import { IconMap } from './Notifications.types'
  import type { NotificationsDTO } from '@/features/api/extranet/dto/Notification/NotificationsDTO'
  import type { NotificationStatutDTO } from '@/features/api/extranet/dto/Notification/NotificationStatutDTO'

  const props = defineProps<{
    listToggleId: number
    unreadList: NotificationsDTO | null
    allList: NotificationsDTO | null
    mode: 'desktop' | 'mobile'
  }>()

  const emit = defineEmits<{
    (e: 'update:listToggleId', val: number): void
    (e: 'markAllAs', statut: NotificationStatutDTO): void
    (e: 'changeNotificationStatut', id: number, statut: NotificationStatutDTO): void
  }>()

  const totalCount = computed(() =>
    props.listToggleId === 0 ? (props.unreadList?.totalRecords ?? 0) : (props.allList?.totalRecords ?? 0),
  )

  function formatDate(date: string) {
    return moment(date).fromNow()
  }
</script>
<style lang="less" scoped>
  @import './NotificationsComponent.less';
</style>
